<template>
	<Modal
		@dismiss="dismiss"
		:custom_style="{ width: '748px', 'max-height': '780px', 'padding-bottom': '24px' }"
	>
		<header class="header-modal">
			<nav class="tab-container radio-group-2">
				<input
					id="apiTab"
					type="radio"
					name="apiTab"
					:checked="currentTab === 'api_data'"
					@input="setTab(tabs.API_DATA)"
				/>
				<label for="apiTab" class="radio-label light">
					<span>Automate</span>
				</label>
				<input
					id="announceTab"
					type="radio"
					name="announceTab"
					:checked="currentTab === 'announce'"
					@input="setTab(tabs.ANNOUNCE)"
				/>
				<label for="announceTab" class="radio-label light">
					<span>Trigger</span>
				</label>
			</nav>
			<button type="button" class="button-icon light button-close" @click="dismiss">
				<img src="../../assets/img/icons/round-close-24px.svg" />
			</button>
		</header>
		<main>
			<section v-if="currentTab === 'api_data'" id="apiSection" class="modal-section">
				<h5>Notification API endpoint</h5>
				<p>To trigger a notification send a POST request to the following API endpoint.</p>
				<p>
					<b>POST</b>&nbsp;
					<code>https://api.gleebeam.com/v1/trigger</code>
				</p>
				<h5 class="mt-32">JSON data for {{ notification.name }}</h5>
				<p>
					Include the following JSON data in the request body. Replace content in
					<code>&lt;&gt;</code> brackets with the content you want displayed in the respective layers.
				</p>
				<pre>{{ layerJSON }}</pre>
				<p>
					Notifications appear on the
					<router-link to="/dashboard" target="_blank">
						<img src="../../assets/img/icons/round-launch-24px.svg" width="16" class="mr-2" />Dashboard
					</router-link>&nbsp;so make sure you have it open somewhere!
				</p>
			</section>
			<section v-if="currentTab === 'announce'" id="announceSection" class="modal-section">
				<h5>Trigger manually</h5>
				<p>
					Fill the input fields (if any) with custom content you want displayed in the notification. Notifications appear on the
					<router-link to="/dashboard" target="_blank">
						<img src="../../assets/img/icons/round-launch-24px.svg" width="16" class="mr-2" />Dashboard
					</router-link>&nbsp;so make sure you have it open somewhere!
				</p>
				<div id="announceForm" v-if="notificationTemplateData">
					<div v-for="layer in notification.template" :key="layer.id">
						<span v-if="layer.dynamic">
							<label :for="layer.id" class="light">{{ layer.name }}</label>
							<input
								v-if="layer.type === 'image'"
								:id="layer.id"
								type="text"
								placeholder="Paste image URL here"
								v-model="notificationTemplateData[layer.api_name]"
							/>
							<textarea
								v-else-if="layer.type === 'text'"
								:id="layer.id"
								placeholder="Write some text here"
								v-model="notificationTemplateData[layer.api_name]"
								:rows="Math.min(notificationTemplateData[layer.api_name].split('\n').length, 5)"
							/>
						</span>
					</div>
				</div>
				<div class="mt-16">
					<button
						type="button"
						class="button primary"
						@click="trigger(notification)"
						:disabled="loading"
					>Trigger</button>
				</div>
			</section>
		</main>
		<footer class="modal-footer">
			<Separator :custom_style="{ 'margin-top': '32px', 'width': '700px' }" />
			<h5>Resources</h5>
			<div class="mt-8">
				<a
					href="https://www.notion.so/pizzabreakfast/Gleebeam-docs-c7ae91ccf40d42b2a38fa90e9e16afd3"
					target="_blank"
				>Gleebeam docs</a>
			</div>
			<div class="mt-8">
				<a href="https://blog.gleebeam.com/category/guides/" target="_blank">Gleebeam guides</a>
			</div>
		</footer>
	</Modal>
</template>

<script>
import Modal from "./modal";
import Separator from "../separator/separator";

export default {
	data() {
		return {
			loading: false,
			announceInterval: 1000,
			tabs: {
				API_DATA: "api_data",
				ANNOUNCE: "announce"
			},
			currentTab: "api_data",
			notificationTemplateData: null
		};
	},
	components: {
		Modal,
		Separator
	},
	props: {
		start_tab: {
			type: String,
			required: false,
			default: "api_data"
		},
		notification: {
			type: Object,
			required: true
		}
	},
	computed: {
		layerJSON() {
			let json = {};

			json.notification_id = this.notification.id;

			this.notification.template.forEach(layer => {
				if (layer.dynamic) {
					let value = layer.type === "image" ? "image URL" : "text";
					json[layer.api_name] = `<your ${value}>`;
				}
			});

			return JSON.stringify(json, null, 4);
		}
	},
	methods: {
		dismiss() {
			this.$emit("dismiss");
		},
		setTab(tab) {
			this.currentTab = tab;
		},
		async trigger(notification) {
			this.loading = true;
			// console.log(notification.template);
			// console.log(notification.layerValuesAsObject);
			const response = await this.$http({
				method: "post",
				url: "https://api.gleebeam.com/v1/trigger",
				data: {
					notification_id: notification.id,
					...this.notificationTemplateData
				}
			});

			setTimeout(() => {
				this.loading = false;
			}, this.announceInterval);

			console.log("Server responded with: " + response.data.message); // eslint-disable-line no-console
		}
	},
	created() {
		this.currentTab = this.start_tab;
		this.notificationTemplateData = this.notification.layerValuesAsObject;
	}
};
</script>

<style scoped>
.modal-section,
.modal-footer {
	padding: 0 24px;
	text-align: left;
}

#announceForm {
	width: 370px;
}

#announceForm textarea {
	width: 100%;
	min-width: 100%;
	max-width: 100%;
	min-height: 36px;
	padding: 8px 8px;
	border: none;
	box-shadow: inset 0px 1px 4px rgba(35, 38, 43, 0.15);
	border-radius: 4px;
	font-weight: normal;
	font-size: 14px;
	line-height: 19px;
	font-weight: normal;
	background-color: var(--light-white);
	-webkit-appearance: none;
}

#announceForm textarea:focus {
	background-color: white;
	outline: none;
	box-shadow: inset 0px 1px 4px rgba(35, 38, 43, 0.3);
}

.header-modal {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: none;
	padding-top: 16px;
}

.tab-container {
	width: 162px;
}
</style>
